import React, { useMemo, useState } from 'react'
import { CCard, CCardBody, CCardHeader, CSmartTable } from '@coreui/react-pro'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import { useChartTheme } from './chartTheme'
import RankedBarList from './RankedBarList'
import {
  ChartSkeleton,
  EmptyState,
  ErrorState,
  computeDelta,
  formatDecimal,
  formatNumber,
} from './common'
import ScreenFlowOffcanvas from './ScreenFlowOffcanvas'

// Search is one box with a field picker, never per-column filters — so every
// column carries filter: false (README "Listing pages and tables").
const columns = [
  { key: 'page_name', label: 'Screen', filter: false, _style: { minWidth: '220px' } },
  { key: 'views', label: 'Views', filter: false, _style: { width: '12%' } },
  { key: 'unique_users', label: 'People', filter: false, _style: { width: '12%' } },
  { key: 'unique_sessions', label: 'Sessions', filter: false, _style: { width: '12%' } },
  { key: 'share_pct', label: 'Share of views', filter: false, _style: { width: '14%' } },
  { key: 'trend', label: 'vs previous', filter: false, sorter: false, _style: { width: '14%' } },
]

const SEARCH_FIELDS = [{ key: 'page_name', label: 'Screen' }]

/**
 * Top ten screens. A ranked list rather than a bar chart: view counts here are
 * small integers, and putting the bar, the name and the exact number on one row
 * reads better than a canvas with an axis. Rows are clickable, so the chart and
 * the table below open the same journey panel.
 */
const TopScreensList = ({ rows, theme, onSelect }) => {
  const items = useMemo(
    () =>
      (rows || []).slice(0, 10).map((row) => ({
        key: row.page_name,
        label: row.page_name,
        value: Number(row.views) || 0,
        share: Number(row.share_pct) || 0,
        people: Number(row.unique_users) || 0,
      })),
    [rows],
  )

  return (
    <RankedBarList
      rows={items}
      color={theme.series[0]}
      valueLabel="views"
      onRowClick={(row) => onSelect(row.key)}
      emptyLabel="No screens visited in this period."
    />
  )
}

const ScreensTab = ({ screens, filters, loading, error, onRetry }) => {
  const theme = useChartTheme()
  const [selected, setSelected] = useState(null)
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [search, setSearch] = useState('')

  const rows = useMemo(() => screens?.rows || [], [screens])
  const hasData = rows.length > 0

  const tableItems = useMemo(() => {
    const mapped = rows.map((row) => ({
      ...row,
      views: Number(row.views) || 0,
      unique_users: Number(row.unique_users) || 0,
      unique_sessions: Number(row.unique_sessions) || 0,
      share_pct: Number(row.share_pct) || 0,
    }))

    if (!search) return mapped

    const needle = search.toLowerCase()
    return mapped.filter((row) =>
      String(row[searchField] || '')
        .toLowerCase()
        .includes(needle),
    )
  }, [rows, search, searchField])

  return (
    <>
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Most visited screens</strong>
          <div className="small text-medium-emphasis">
            Top 10 by screen views. Select any row in the table below to see how people reach it.
          </div>
        </CCardHeader>
        <CCardBody>
          {loading && <ChartSkeleton height={300} />}
          {!loading && error && <ErrorState message={error} onRetry={onRetry} height={300} />}
          {!loading && !error && !hasData && <EmptyState height={300} />}
          {!loading && !error && hasData && (
            <TopScreensList rows={rows} theme={theme} onSelect={setSelected} />
          )}
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <strong>All screens</strong>
          <div className="small text-medium-emphasis">
            {screens?.total
              ? `${formatNumber(screens.total)} screens visited in this period. `
              : ''}
            Search, sort, or select a screen for its journey.
          </div>
        </CCardHeader>
        <CCardBody>
          {loading && <ChartSkeleton height={260} />}
          {!loading && error && <ErrorState message={error} onRetry={onRetry} height={260} />}
          {!loading && !error && !hasData && <EmptyState height={260} />}
          {!loading && !error && hasData && (
            <>
              <TableSearchToolbar
                fields={SEARCH_FIELDS}
                field={searchField}
                onFieldChange={(next) => {
                  setSearchField(next)
                  setSearch('')
                }}
                value={search}
                onSearch={setSearch}
                placeholder="e.g. MatchScorecard"
              />
              <div className="ge-datatable ge-datatable--no-select">
                <CSmartTable
                  items={tableItems}
                  columns={columns}
                  columnSorter
                  itemsPerPage={15}
                  itemsPerPageSelect
                  pagination
                  clickableRows
                  loading={loading}
                  noItemsLabel="No screens found"
                  onRowClick={(item) => setSelected(item.page_name)}
                  sorterValue={{ column: 'views', state: 'desc' }}
                  tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                  scopedColumns={{
                    page_name: (item) => (
                      <td>
                        <span className="av2-screen-name">{item.page_name}</span>
                      </td>
                    ),
                    views: (item) => <td className="fw-semibold">{formatNumber(item.views)}</td>,
                    unique_users: (item) => <td>{formatNumber(item.unique_users)}</td>,
                    unique_sessions: (item) => <td>{formatNumber(item.unique_sessions)}</td>,
                    share_pct: (item) => (
                      <td className="text-medium-emphasis">{formatDecimal(item.share_pct, 1)}%</td>
                    ),
                    trend: (item) => {
                      const delta = computeDelta(item.views, item.previous_views)
                      return (
                        <td>
                          <span className={`text-${delta.color}`}>{delta.label}</span>
                        </td>
                      )
                    },
                  }}
                />
              </div>
            </>
          )}
        </CCardBody>
      </CCard>

      <ScreenFlowOffcanvas
        visible={Boolean(selected)}
        pageName={selected}
        filters={filters}
        onClose={() => setSelected(null)}
      />
    </>
  )
}

export default ScreensTab
